import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    HostBinding,
    OnChanges,
    OnInit,
    SimpleChanges,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    ArticleSearchFailure,
    ArticleSearchListType,
    AddProductToBasketEvent,
    IArticleSearchSourceState,
    articleSearchFailureOf,
} from '@lib/article';
import { IOciSearchResponse, OciArticle } from '../types/IOciSearchResponse';
import { OciDataService } from '../oci-data.service';
import { finalize } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { OciBasketProductDto } from '../types/IOciBasketProductDto';
import { OciBackgroundSearchService } from '../oci-background-search.service';
import { IArticleCardBasketRequest, IArticleCardItem } from '@lib/shared/article-card/ui';
import { toArticleCardItem } from './oci-search-card.mapper';

@Component({
    selector: 'oci-search-results',
    templateUrl: './oci-search-results.component.html',
    styleUrls: ['./oci-search-results.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OciSearchResultsComponent implements OnChanges, OnInit {
    private readonly ociService = inject(OciDataService);
    private readonly destroyRef = inject(DestroyRef);
    readonly backgroundSearchService = inject(OciBackgroundSearchService);

    readonly query = input.required<string>();
    readonly catalogId = input.required<string>();
    readonly listType = input<ArticleSearchListType>(ArticleSearchListType.GRID);
    readonly catalogName = input<string>('');
    readonly supplierGroupId = input<string>(); //Maybe the supplierCompanyId should be used ???

    /** Meldet Lade-, Treffer- und Fehlerzustand an die Quellen-Seitenleiste. */
    readonly searchStateChanged = output<IArticleSearchSourceState>();

    @HostBinding('class') hostClasses = 'full-width';
    public ArticleSearchListType = ArticleSearchListType;

    /** Bis zur ersten Antwort `undefined`; nach einem Fehler leer. */
    readonly articles = signal<readonly OciArticle[] | undefined>(undefined);
    readonly loading = signal(false);
    readonly addButtonDisabled = signal(false);

    /** Ursache des letzten Fehlschlags, `null` nach einer erfolgreichen Antwort. */
    private readonly failure = signal<ArticleSearchFailure | null>(null);

    /** Die Ergebnisliste haengt an die Zeitueberschreitung einen eigenen Hinweis. */
    readonly timeout = computed(() => this.failure() === ArticleSearchFailure.TIMEOUT);

    readonly noArticlesFound = computed(() => this.articles()?.length === 0 && !this.timeout());

    /** Die Treffer als Eingabemodell der gemeinsamen Artikel-Card. */
    readonly cardItems = computed<readonly IArticleCardItem[]>(() =>
        (this.articles() ?? []).map((article) => toArticleCardItem(article, this.catalogId(), this.catalogName())),
    );

    /** Eine verspaetete Antwort zum vorigen Suchbegriff darf das neue Ergebnis nicht ueberschreiben. */
    private searchSubscription?: Subscription;

    public search(): void {
        this.loading.set(true);
        this.searchStateChanged.emit({ count: null, loading: true, failed: false });
        this.searchSubscription?.unsubscribe();
        this.searchSubscription = this.ociService
            .search(this.query(), this.catalogId())
            // Eine Antwort nach dem Entfernen der Komponente wuerde sonst auf einer bereits
            // geschlossenen Ausgabe landen (NG0953).
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (data: IOciSearchResponse) => {
                    const articles = data.members.map((articleDto) => new OciArticle(articleDto));
                    this.failure.set(null);
                    this.articles.set(articles);
                    this.loading.set(false);
                    this.searchStateChanged.emit({ count: articles.length, loading: false, failed: false });
                },
                // Jeder Fehler beendet die Quelle als "ausgefallen" - fuer die Seitenleiste ist das
                // ausdruecklich nicht dasselbe wie "keine Treffer" (BR5 UC_SEARCH_1001). Mit Ursache,
                // damit die Seitenleiste den passenden Hinweis zeigt; erneut versuchen laesst sich
                // die Suche immer.
                error: (error: unknown) => {
                    const failure = articleSearchFailureOf(error);
                    this.failure.set(failure);
                    this.articles.set([]);
                    this.loading.set(false);
                    this.searchStateChanged.emit({
                        count: null,
                        loading: false,
                        failed: true,
                        failure,
                        canRetry: true,
                    });
                },
            });
    }

    public onBasketRequested(request: IArticleCardBasketRequest): Promise<void> {
        return this.addOciProductToBasketFromEproc({
            id: request.item.id,
            type: request.item.type,
            basketAmount: request.quantity,
        });
    }

    public addOciProductToBasketFromEproc(addProductToBasketEvent: AddProductToBasketEvent): Promise<void> {
        this.addButtonDisabled.set(true);

        const productExternalId = addProductToBasketEvent.id;

        const quantity = addProductToBasketEvent.basketAmount;

        // Find the correct article by matching against possible identifiers
        const foundArticle = (this.articles() ?? []).find(
            (article) =>
                article.extProductId === productExternalId ||
                (article.vendorMat != null && String(article.vendorMat) === productExternalId) ||
                (article.matnr != null && String(article.matnr) === productExternalId),
        );

        const productForBasket: OciBasketProductDto = new OciBasketProductDto(
            foundArticle,
            productExternalId,
            this.supplierGroupId(),
        );

        return this.backgroundSearchService
            .addOciProductToBasketFromEproc(productForBasket, this.catalogId(), quantity)
            .pipe(finalize(() => this.addButtonDisabled.set(false)))
            .toPromise();
    }

    ngOnInit(): void {
        this.search();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['query'] && !changes['query'].isFirstChange()) {
            this.search();
        }
    }
}
